/**
 * The palette keys the CMS gives the colour engine's roles from their names (Primær, Accent, Baggrund, Tekst, Mørk
 * baggrund, Lys baggrund). Templates name a role by its key, so a template's section takes the role's colour on any
 * look-system website. scripts/design/cms2-look-colours.mjs mirrors it.
 */
export const ROLE_PALETTE_KEYS = {
  brand: "primaer",
  accent: "accent",
  background: "baggrund",
  text: "tekst",
  band: "moerk-baggrund",
  soft: "lys-baggrund",
} as const;

export type PaletteRole = keyof typeof ROLE_PALETTE_KEYS;

/** The role whose colour stands in where a website's palette has no colour for a role. */
const STAND_IN: Partial<Record<PaletteRole, PaletteRole>> = {
  accent: "brand",
  band: "text",
  soft: "background",
};

type Json = Record<string, unknown>;

const record = (value: unknown): Json =>
  value && typeof value === "object" && !Array.isArray(value)
    ? (value as Json)
    : {};

/** A palette key after the palette's redirects; undefined for a key no palette colour answers. */
export function resolvePaletteKey(
  colorSystem: unknown,
  key: string,
): string | undefined {
  const system = record(colorSystem);
  const keys = new Set(
    (Array.isArray(system.palette) ? system.palette : []).map(
      (entry) => record(entry).key,
    ),
  );
  const redirects = record(system.redirects);
  const seen = new Set<string>();
  for (
    let current: unknown = key;
    typeof current === "string" && !seen.has(current);
    current = redirects[current]
  ) {
    if (keys.has(current)) return current;
    seen.add(current);
  }
  return undefined;
}

/** The palette colour a role uses: `derived.roles` names it, else a role chosen as a palette colour in `globals`. */
function roleKey(colorSystem: unknown, role: PaletteRole): string | undefined {
  const system = record(colorSystem);
  for (const candidate of [
    record(record(system.derived).roles)[role],
    record(record(system.globals)[role]).ref,
  ])
    if (typeof candidate === "string") {
      const key = resolvePaletteKey(colorSystem, candidate);
      if (key) return key;
    }
  return undefined;
}

/**
 * The palette colour a role's key stands for on a website whose palette has no colour (and no redirect) under that key:
 * the colour with the role, else the colour of the role standing in for it. Undefined for any other key.
 */
export function rolePaletteKey(
  colorSystem: unknown,
  key: string,
): string | undefined {
  const role = (Object.keys(ROLE_PALETTE_KEYS) as PaletteRole[]).find(
    (candidate) => ROLE_PALETTE_KEYS[candidate] === key,
  );
  if (!role || resolvePaletteKey(colorSystem, key)) return undefined;
  const standIn = STAND_IN[role];
  return (
    roleKey(colorSystem, role) ??
    (standIn ? roleKey(colorSystem, standIn) : undefined)
  );
}
